<template>
  <div>
    <div
      v-if="isInInit || (!calList.length && !data?.length)"
      class="flex gap-x-[0px]"
    >
      <CardItem
        v-for="x in itemNumber"
        :key="x"
        :showCreate="false"
        :showPreview="false"
      >
        <template #cover>
          <div class="bg-back-4"></div>
        </template>
        <template #info>
          <div
            :style="height ? `height: ${height}px` : ''"
            :class="$style['load-btn']"
          >
            <a-skeleton-button active class="h-full" size="small" block />
          </div>
        </template>
      </CardItem>
    </div>
    <div v-else class="relative">
      <a-carousel v-bind="options" ref="carouselRef" :class="$style.suggest">
        <div
          v-for="x in dataSource"
          :key="x.id"
          :class="['pb-[18px] pt-[15px] px-[7px] card-container']"
        >
          <CardItem
            :data="x"
            class="h-full w-full"
            :style="`height: ${itemHeight}px`"
            :collect="collect"
            :hover="hover"
            :showCreate="!isBestPractice"
            :share="share"
            resolution="horizontal"
            :small="small"
            @click="onHandClick(x)"
            @change="
              props.reload ? props.reload($event, x) : onChange($event, x)
            "
          />
        </div>
        <div
          v-if="dataSource.length > itemNumber"
          class="px-[10px] pb-[2px] pt-[7px]"
        >
          <div class="go-back" :style="listHeight" @click="gotoFirst">
            <div
              class="absolute top-0 left-0 w-full h-full flex-center flex-col"
            >
              <div
                class="w-[48px] h-[48px] bg-back-3 rounded-[50%] flex-center"
              >
                <Resource :src="ArrowSvgUrl" class="w-[21px]" />
              </div>
              <span class="text-14 mt-[15px] text-font-80">
                {{ $t('hui-dao-shou-ye') }}
              </span>
            </div>
          </div>
        </div>
      </a-carousel>
      <div
        v-if="currentIndex > 0"
        :class="$style['slick-arrow']"
        class="left-[-14px]"
        @click="carouselPrev"
      >
        <iconpark-icon
          name="arrowright"
          class="text-[18px] text-font-60 rotate-[180deg]"
          :class="$style['arrow']"
        ></iconpark-icon>
      </div>
      <div
        v-if="
          currentIndex + itemNumber <
          dataSource.length + (dataSource.length > itemNumber ? 1 : 0)
        "
        :class="$style['slick-arrow']"
        class="right-[-14px]"
        @click="carouselNext"
      >
        <iconpark-icon
          name="arrowright"
          class="text-[18px] text-font-60"
          :class="$style['arrow']"
        ></iconpark-icon>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CardItem from './cardItem.vue'
import type { IResponse, Template } from '~/api/interface'
import Resource from '~/components/resource'
import { useScrollList } from '~/hooks/useScrollList'
import { useTemplateDetail } from '~/hooks/useModal/modals/template'
import ArrowSvgUrl from '~/assets/svg/arrow.svg?url'
import { useStarPatchList } from '~/hooks/useStarPatch'
const props = withDefaults(
  defineProps<{
    api: (
      params: any
    ) => Promise<IResponse<{ count?: number; results?: Template[] }>>
    id: string
    itemHeight?: number
    itemNumber?: number
    collect?: boolean
    share?: boolean
    hover?: boolean
    reload?: ([key, value]: [string, any], data: Template) => void
    templateSource?: TEMPLATE_SOURCE
    trackParams?: any
    height?: number
    data?: Template[]
    small?: boolean
    isBestPractice?: boolean
    filterId?: number
    isSeries?: boolean // 是否是系列课程
    position?: number // 课程系列位置
  }>(),
  {
    itemNumber: 5,
    collect: true,
    hover: true,
    small: false,
    isBestPractice: false,
    isSeries: false,
    position: 0,
    itemHeight: 188,
  }
)

const { list, next, refresh, total, current, pending } = useScrollList(
  (params) => props.api({ ...params, query_star: import.meta.client }),
  {
    pageSize: props.itemNumber * 2,
    key: props.id,
  }
)

const { calList } = useStarPatchList(list, 3)

const isInInit = computed(() => {
  return current.value === 1 && pending.value
})

const dataSource = computed(() => {
  if (props.data) {
    return props.data
  }

  const l = new Array(total.value - calList.value.length).fill({} as any)
  return [...calList.value, ...l].filter((x) =>
    props.filterId ? x.id !== props.filterId : true
  )
})

const attrs = useAttrs()
const carouselRef = ref()
const currentIndex = ref(0)

const listHeight = computed(() => {
  const h = carouselRef.value?.innerSlider.slideHeight || 0
  return { height: `${h - 25}px` }
})

const gotoFirst = () => {
  carouselRef.value?.goTo(0)
}

const carouselPrev = () => {
  carouselRef.value?.prev()
}

const carouselNext = async () => {
  if (!props.data) {
    if (calList.value.length <= currentIndex.value + props.itemNumber * 2) {
      await next()
    }
  }
  carouselRef.value?.next()
}

const onChange = ([key, value]: [string, any], data: Template) => {
  const item: any = calList.value.find((item) => item.id === data.id)
  if (item) {
    item[key] = value
  }
}

const resourceLocation = injectResourceLocation()

const useInfo = useUserInfo()

const onHandClick = (data: Template) => {
  // if (props.templateSource === TEMPLATE_SOURCE.INDUSTRY_SIMILAR) {
  //   trackEvent(TRACK_EVENT.CLICK_INDUSTRY_OTHER_CARD, {
  //     industry: data.name,
  //   })
  // }

  if (resourceLocation?.source === SOURCE.INDUSTRY_DETAIL) {
    trackEvent(TRACK_EVENT.CLICK_INDUSTRY_OTHER_CARD, {
      ...props.trackParams,
      card_name: data.name,
      source: resourceLocation?.source,
    })
  } else {
    const params = {
      ...props.trackParams,
      template_id: props.filterId,
      source: resourceLocation?.source,
      org_id: useInfo.value?.organization_id,
    }

    trackEvent(TRACK_EVENT.CLICK_TEMPLATE_PREVIEW, params)
  }

  if (attrs.onItemClick) {
    ;(attrs.onItemClick as any)(data, calList.value)
  } else {
    useTemplateDetail(
      calList.value,
      data,
      {
        templateSource: props.templateSource,
        source: resourceLocation?.source,
      },
      props.reload
    )
  }

  if (props.data?.length && !props.isSeries) {
    currentIndex.value = 0
    gotoFirst()
  }
}

const onAfterChange = (current: number) => {
  currentIndex.value = current
}

const options = {
  dots: false,
  arrows: false,
  slidesToShow: props.itemNumber,
  slidesToScroll: props.itemNumber,
  infinite: false,
  afterChange: onAfterChange,
}

defineExpose({
  reload: async () => {
    currentIndex.value = 0
    await refresh()
  },
  change: onChange,
})

onMounted(() => {
  // 教程系列视频需要在指定位置展示
  if (props.isSeries && props?.position > props.itemNumber) {
    const pages = Math.ceil(props.position / props.itemNumber)
    currentIndex.value = pages - 1
    setTimeout(() => {
      carouselRef.value?.goTo(props.position)
    }, 200)
  }
})
</script>

<style lang="scss" module>
.cardImg {
  @apply absolute top-0 left-0 h-full;
  width: 100% !important;
  :global(.ant-skeleton-image) {
    width: 100% !important;
    height: 100% !important;
  }
}
.load-btn {
  // height: 100%;
  min-height: 20px;
  :global(.ant-skeleton-button) {
    height: 100% !important;
    min-height: 20px;
  }
}
.slick-arrow {
  @apply absolute top-[calc(50%-17px)] w-[34px] h-[34px] flex-center rounded-[50%] backdrop-blur-[16px] cursor-pointer;
  z-index: 1;
  background: var(--back-bg-80);
  box-shadow: 0px 0px 5px 0px rgba(0, 26, 133, 0.15);
  transition: all 0.1s ease;
  &:hover {
    transform: translateY(-3px) scale(1.08);
    box-shadow: 2px 0px 16px 0px rgba(0, 26, 133, 0.1);
    backdrop-filter: blur(1.6px);
  }
  img {
    @apply w-[16px] h-[16px];
  }
  &:hover {
    .arrow {
      @apply text-primary;
    }
  }
}

.suggest {
  margin: -7px -10px -16px;

  &:hover {
    :global(.slick-arrow) {
      display: flex !important;
      background: rgba(255, 255, 255, 1) !important;
    }
  }
  :global(.go-back) {
    @apply rounded-[10px] bg-back-1 overflow-hidden cursor-pointer relative;
    border: 1px solid var(--back-3);
    transition: all 0.3s ease;
    &:hover {
      transform: translateY(-6px);
      box-shadow: 0px 2px 12px 0px rgba(4, 14, 48, 0.1);
    }
  }
}
</style>
